import { APP_INITIALIZER, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgReduxModule } from '@angular-redux/store';
import { HttpClient, HttpClientModule } from '@angular/common/http';
// Ag-Grid
import { AgGridModule } from 'ag-grid-angular';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToasterModule } from 'angular2-toaster';
/*
 * Platform and Environment providers/directives/pipes
 */
import { DefaultTranslationParser, SERVICE_PROVIDERS } from './shared/services';
import { ENTITY_FORM_SERVICES } from './shared/services/entityFormServices';
import { HELPER_PROVIDERS } from './shared/helpers';

import { ROUTES } from './app.routes';
// App is our top level component
import {
  TranslateLoader,
  TranslateModule,
  MissingTranslationHandler,
  TranslateParser,
} from '@ngx-translate/core';
import { AppComponent } from './app.component';
import {
  CompanySelectionComponent,
  HomeComponent,
} from './components/app/home';

import { NoContentComponent } from './components/app/no-content';
import { XVIErrorPageComponent } from './components/app/error-page';
import { LoginComponent } from './components/app/login';
import { IframeModeComponent } from './components/app/iframe-mode';
import { LogoutComponent } from './components/app/login/logout.component';
import { NotificationsContainerComponent } from './containers/notifications';
import { XenialUxModule } from './xenial-ux';
// app modules
import { XVIComponentsModule } from './components/xvi.components.module';

/*
 * @angular-redux/store Store
 */
import { STORE_PROVIDERS } from './store';

// Custom pipes
import { MainPipe } from './shared/helpers/pipes/main-pipe.module';

import { MiddlewaresModule } from './middlewares/middlewares.module';
import { DMSharedServiceModule } from '@geniuspos/ui-shared/modules/xdm-shared-service';
import { xviFrameworkComponents } from './components/xvi-grid/cell-helpers';
import { XviDirectivesModule } from './components/directives/directives.module';
import { LandingPageComponent } from './containers/landing-page/landing-page.component';
import { TransferEditContainerComponent } from './containers/transfer/containers';
import { SW_CALCULATION_SERVICE_PROVIDERS } from './shared/services/stand-worksheet/calculation-services';
import { ENTITY_FORM_PROVIDER } from './shared/services/entityFormServicesProvider';
import { REPLENISH_DATA_SERVICE_PROVIDERS } from './shared/services/replenish-worker/replenish-data';
import { OrdersEditContainerComponent } from './containers/orders/containers';
import { XuxDragModule } from './components/xux-dnd/xux-dnd.module';
import { ThemeService } from '@shared/services/theme/theme.service';
import { TranslationLoaderService } from './shared/services/loaders/translation-loaders.service';
import { TranslationService } from './shared/services/translate/translation.service';
import { DEFAULT_LOCALE } from '@config/global-constants';
import { DefaultMissingTranslationHandler } from './i18n/default-missing-translation.handler';
import { I18N_SERVICES } from './i18n/services';
import { ServiceProvider } from './shared/services/service-provider.service';
import { AgGridKeyboardNavigationService } from './shared/common/ag-grid-navigation';

const initializeTheme = (themeService: ThemeService) => () =>
  themeService.fetchTheme();

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    LogoutComponent,
    AppComponent,
    HomeComponent,
    NoContentComponent,
    XVIErrorPageComponent,
    LoginComponent,
    IframeModeComponent,
    CompanySelectionComponent,
    NotificationsContainerComponent,
    LandingPageComponent,
    TransferEditContainerComponent,
    OrdersEditContainerComponent,
    ...xviFrameworkComponents,
  ],
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: DEFAULT_LOCALE,
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
        deps: [HttpClient, TranslationService, TranslationLoaderService],
      },
      parser: {
        provide: TranslateParser,
        useClass: DefaultTranslationParser,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: DefaultMissingTranslationHandler,
      },
      extend: true,
    }),
    // import Angular's modules
    XVIComponentsModule,
    XuxDragModule,
    BrowserModule,
    FormsModule,
    HttpClientModule,
    NgReduxModule,
    AgGridModule,
    ReactiveFormsModule,
    XviDirectivesModule,
    AgGridModule.withComponents([
      // xviFrameworkComponents is the second iteration grid helpers
      ...xviFrameworkComponents,
      // customGridComponents is the third XUX iteration grid helpers
    ]),
    BrowserAnimationsModule,
    ToasterModule.forRoot(),
    MainPipe,
    XenialUxModule.forRoot(),
    MiddlewaresModule,
    RouterModule.forRoot(ROUTES, { useHash: true }),
    DMSharedServiceModule,
  ],
  providers: [
    // expose our Services and Providers into Angular's dependency injection
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      deps: [ThemeService],
      multi: true,
    },
    SERVICE_PROVIDERS,
    ENTITY_FORM_SERVICES,
    HELPER_PROVIDERS,
    STORE_PROVIDERS,
    SW_CALCULATION_SERVICE_PROVIDERS,
    REPLENISH_DATA_SERVICE_PROVIDERS,
    ENTITY_FORM_PROVIDER,
    I18N_SERVICES,
    ServiceProvider,
    AgGridKeyboardNavigationService,
  ],
  schemas: [NO_ERRORS_SCHEMA],
})
export class AppModule {}
